import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Slider from '../../components/Slider'; // Reusable slider preview component
import UploadImage from '../../components/UploadImage';
import '../../styles/SetSlider.css';
import backendUrl from '../../BackendUrl';

const SetSlider = () => {
  // List of sliders to display
  const [sliders, setSliders] = useState([]);
  // Modal visibility for add/edit
  const [isCanvasVisible, setIsCanvasVisible] = useState(false);
  // Whether we are editing or adding a new slider
  const [isEditMode, setIsEditMode] = useState(false);
  // Slider currently being edited (null if adding)
  const [selectedSlider, setSelectedSlider] = useState(null);

  // Form state for slider name and description
  const [formData, setFormData] = useState({
    slider: '',
    description: ''
  });

  // Slider type: 'vehicle' or 'offer'
  const [sliderType, setSliderType] = useState('');
  // For vehicle-type slider: list of vehicles fetched from backend
  const [vehicles, setVehicles] = useState([]);
  // Selected vehicle _id (only applicable when sliderType is "vehicle")
  const [selectedVehicle, setSelectedVehicle] = useState('');

  const adminToken = localStorage.getItem('adminToken');

  // Fetch all sliders when the page loads
  useEffect(() => {
    fetchSliders();
  }, []);

  // Fetch vehicles when sliderType changes to "vehicle"
  useEffect(() => {
    if (sliderType === 'vehicle') {
      fetchVehicles();
    }
  }, [sliderType]);

  const fetchSliders = async () => {
    try {
      const res = await axios.get(`${backendUrl}/api/sliders/get-slider`);
      setSliders(res.data);
    } catch (error) {
      console.error('Error fetching sliders:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const res = await axios.get(`${backendUrl}/api/vehicles/get-model`);
      setVehicles(res.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  // Handle change for the radio buttons selecting slider type
  const handleSliderTypeChange = (e) => {
    setSliderType(e.target.value);
    setSelectedVehicle(''); // Reset any previously selected vehicle
  };

  // Handle form text input changes
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle vehicle dropdown change
  const handleVehicleSelectChange = (e) => {
    setSelectedVehicle(e.target.value);
  };

  // Open the canvas to add a new slider
  const handleAddClick = () => {
    setIsEditMode(false);
    setSelectedSlider(null);
    setFormData({ slider: '', description: '' });
    setSliderType('');
    setSelectedVehicle('');
    setIsCanvasVisible(true);
  };

  // Open the canvas to edit an existing slider
  const handleEditClick = (sliderItem) => {
    setIsEditMode(true);
    setSelectedSlider(sliderItem);
    setFormData({
      slider: sliderItem.slider,
      description: sliderItem.description,
    });
    // Determine type based on the link value
    if (sliderItem.link.startsWith('/vehicles')) {
      setSliderType('vehicle');
      const parts = sliderItem.link.split('/');
      if (parts.length >= 3) setSelectedVehicle(parts[2]);
    } else {
      setSliderType('offer');
    }
    setIsCanvasVisible(true);
  };

  // Cancel/close the modal canvas
  const handleCancel = () => {
    setIsCanvasVisible(false);
  };

  // Handle form submission for both adding and updating a slider
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    let link = '';
    if (sliderType === 'vehicle') {
      if (!selectedVehicle) {
        alert('Please select a vehicle.');
        return;
      }
      link = `/vehicles/${selectedVehicle}`;
    } else if (sliderType === 'offer') {
      link = '/offers';
    } else {
      alert('Please select a slider type.');
      return;
    }

    const sliderData = {
      slider: formData.slider,
      description: formData.description,
      link: link,
    };

    try {
      if (isEditMode && selectedSlider) {
        // Update an existing slider
        await axios.put(
          `${backendUrl}/api/sliders/update-slider/${selectedSlider._id}`,
          sliderData,
          { headers: { Authorization: `Bearer ${adminToken}` } }
        );
      } else {
        // Add a new slider
        await axios.post(
          `${backendUrl}/api/sliders/add-slider`,
          sliderData,
          { headers: { Authorization: `Bearer ${adminToken}` } }
        );
      }
      fetchSliders();
      setIsCanvasVisible(false);
    } catch (error) {
      console.error('Error saving slider:', error);
      alert('Error saving slider.');
    }
  };

  // Delete slider handler
  const handleDelete = async (sliderId) => {
    if (window.confirm('Are you sure you want to delete this slider?')) {
      try {
        await axios.delete(
          `${backendUrl}/api/sliders/delete-slider/${sliderId}`,
          { headers: { Authorization: `Bearer ${adminToken}` } }
        );
        fetchSliders();
      } catch (error) {
        console.error('Error deleting slider:', error);
        alert('Error deleting slider.');
      }
    }
  };

  // Handle image upload success from the UploadImage component
  const handleUploadSuccess = (data) => {
    console.log('Image uploaded successfully:', data);
    fetchSliders();
  };

  return (
    <div className="set-slider-page">
      <button onClick={handleAddClick} className="add-slider-btn">
        Add Slider
      </button>

      {/* Preview area for the slider using the reusable Slider component */}
      <div className="slider-preview">
        <Slider />
      </div>

      {/* List of existing sliders with edit, delete, and image upload options */}
      <div className="sliders-list">
        {sliders.map((sliderItem) => (
          <div key={sliderItem._id} className="slider-card">
            <img
              src={sliderItem.sliderImg || 'default-slider-image-url'}
              alt={sliderItem.slider}
              className="slider-image"
            />
            <div className="slider-details">
              <h3>{sliderItem.slider}</h3>
              <p>{sliderItem.description}</p>
              <p>Link: {sliderItem.link}</p>
            </div>
            <div className="slider-actions">
              <button onClick={() => handleEditClick(sliderItem)}>Edit</button>
              <button onClick={() => handleDelete(sliderItem._id)}>
                Delete
              </button>
            </div>
            <UploadImage
              uploadUrl="/api/sliders/upload-slider"
              id={sliderItem._id}
              onUploadSuccess={handleUploadSuccess}
            />
          </div>
        ))}
      </div>

      {/* Modal canvas for adding or editing a slider */}
      {isCanvasVisible && (
        <div className="slider-canvas-overlay">
          <div className="slider-canvas">
            <button className="close-button" onClick={handleCancel}>
              X
            </button>
            <h2>{isEditMode ? 'Edit Slider' : 'Add Slider'}</h2>
            <form onSubmit={handleFormSubmit} className="slider-form">
              <div className="form-group">
                <label>Slider Type:</label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="sliderType"
                      value="vehicle"
                      checked={sliderType === 'vehicle'}
                      onChange={handleSliderTypeChange}
                      disabled={isEditMode} 
                    />
                    Vehicle
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="sliderType"
                      value="offer"
                      checked={sliderType === 'offer'}
                      onChange={handleSliderTypeChange}
                      disabled={isEditMode}
                    />
                    Offer
                  </label>
                </div>
              </div>

              {sliderType === 'vehicle' && (
                <div className="form-group">
                  <label>Select Vehicle Model:</label>
                  <select
                    value={selectedVehicle}
                    onChange={handleVehicleSelectChange}
                    required
                  >
                    <option value="">-- Select Vehicle --</option>
                    {vehicles.map((vehicle) => (
                      <option key={vehicle._id} value={vehicle._id}>
                        {vehicle.model}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Slider Name:</label>
                <input
                  type="text"
                  name="slider"
                  value={formData.slider}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Description:</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                ></textarea>
              </div>

              <button type="submit" className="submit-btn">
                {isEditMode ? 'Update Slider' : 'Add Slider'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SetSlider;